import { type QueryKey, useInfiniteQuery, type UseInfiniteQueryOptions } from '@tanstack/react-query';

import { useCloudPlatformDiscovery } from '../utils/hooks';
import { jobName } from './gen/ai/h2o/mlops/batch/v1/job_pb';
import { deploymentName } from './gen/ai/h2o/mlops/deployer/v2/deployment_pb';
import type {
  ListAggregatesRequest,
  ListAggregatesResponse,
  ListBaselineAggregatesRequest,
  ListBaselineAggregatesResponse,
} from './gen/ai/h2o/mlops/monitoring/v2/aggregate_service_pb';
import { listAggregates, listBaselineAggregates } from './mlops.monitoring.functions';

const cacheKeys = {
  aggregatesAll: (
    workspaceId: string,
    resourceId: string,
    experimentId: string,
    params?: Pick<ListAggregatesRequest, `filter`>
  ) => {
    const baseKey: QueryKey = [
      `mlops`,
      `monitoring`,
      workspaceId,
      `resource`,
      resourceId,
      `experiment`,
      experimentId,
      `aggregates`,
      `all`,
    ];
    if (!params) return baseKey;
    const { filter } = params;
    return [...baseKey, { filter: filter || `` }];
  },
  baselineAggregatesAll: (
    workspaceId: string,
    resourceId: string,
    experimentId: string,
    params?: Pick<ListBaselineAggregatesRequest, `filter`>
  ) => {
    const baseKey: QueryKey = [
      `mlops`,
      `monitoring`,
      workspaceId,
      `resource`,
      resourceId,
      `experiment`,
      experimentId,
      `baselineAggregates`,
      `all`,
    ];
    if (!params) return baseKey;
    const { filter } = params;
    return [...baseKey, { filter: filter || `` }];
  },
};

export const useQueryAggregates = <TData = ListAggregatesResponse>(
  request: Omit<ListAggregatesRequest, `pageToken` | `parent`> & {
    deploymentId?: string;
    jobId?: string;
    experimentId: string;
    workspaceId: string;
  },
  options?: Omit<
    UseInfiniteQueryOptions<ListAggregatesResponse, unknown, TData>,
    `getNextPageParam` | `queryFn` | `queryKey`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const { deploymentId, jobId, experimentId, workspaceId, ...baseRequest } = request;
  const resourceId = deploymentId || jobId || '';
  const baseOptions: UseInfiniteQueryOptions<ListAggregatesResponse, unknown, TData> = {
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
    queryFn: ({ pageParam, signal }) => {
      const parentResource = deploymentId
        ? deploymentName.compile({
            workspace: workspaceId,
            deployment: deploymentId,
          })
        : jobId
        ? jobName.compile({
            workspace: workspaceId,
            job: jobId,
          })
        : '';
      const parentExperiment = `${parentResource}/experiments/${experimentId}`;
      return listAggregates(
        mlopsApiUrl,
        {
          ...baseRequest,
          pageToken: pageParam,
          parent: parentExperiment,
        },
        { signal }
      );
    },
    queryKey: cacheKeys.aggregatesAll(workspaceId, resourceId, experimentId, request),
  };
  return useInfiniteQuery({ ...options, ...baseOptions });
};

export const useQueryBaselineAggregates = <TData = ListBaselineAggregatesResponse>(
  request: Omit<ListBaselineAggregatesRequest, `pageToken` | `parent`> & {
    deploymentId?: string;
    jobId?: string;
    experimentId: string;
    workspaceId: string;
  },
  options?: Omit<
    UseInfiniteQueryOptions<ListBaselineAggregatesResponse, unknown, TData>,
    `getNextPageParam` | `queryFn` | `queryKey`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const { deploymentId, jobId, experimentId, workspaceId, ...baseRequest } = request;
  const resourceId = deploymentId || jobId || '';
  const baseOptions: UseInfiniteQueryOptions<ListBaselineAggregatesResponse, unknown, TData> = {
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
    queryFn: ({ pageParam, signal }) => {
      const parentResource = deploymentId
        ? deploymentName.compile({
            workspace: workspaceId,
            deployment: deploymentId,
          })
        : jobId
        ? jobName.compile({
            workspace: workspaceId,
            job: jobId,
          })
        : '';
      const parentExperiment = `${parentResource}/experiments/${experimentId}`;
      return listBaselineAggregates(
        mlopsApiUrl,
        {
          ...baseRequest,
          pageToken: pageParam,
          parent: parentExperiment,
        },
        { signal }
      );
    },
    queryKey: cacheKeys.baselineAggregatesAll(workspaceId, resourceId, experimentId, request),
  };
  return useInfiniteQuery({ ...options, ...baseOptions });
};
